import { Box, Divider, Drawer, IconButton, Stack, Typography, useMediaQuery, useTheme, Fab } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';
import React from 'react';
import { useSelector } from 'store';
import { gridSpacing } from 'store/constant';
import { closeRightSidebar } from 'store/slices/dual-sidebar';
import MainCard from 'ui-component/cards/MainCard';
import CloseIcon from '@mui/icons-material/Close';
import { ellipsisTextOverflowStyles } from 'themes/common/typography';

interface RightAppSidebarProps {}

const RightAppSidebar: React.FC<RightAppSidebarProps> = () => {
  const { borderRadius } = useConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { open, width, title, hideHeader, onClose, activeId } = useSelector(
    (state) => state.dualSidebar.right,
  );

  // Handle ESC key to close sidebar on mobile
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open && isMobile) {
        onClose ? onClose() : closeRightSidebar();
      }
    };

    if (open && isMobile) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, isMobile, onClose]);

  const drawerBG = theme.palette.background.paper;
  
  // Check if this is the templates preview sidebar
  const isTemplatesPreview = activeId === 'templates-page-preview-sidebar';

  return (
    <Drawer
      sx={{
        width: open ? (isMobile ? '90vw' : width) : 0, // 90% of viewport width on mobile
        flexShrink: 0,
        zIndex: { xs: 1100, md: 0 },
        height: '100%',
        transition: isTemplatesPreview 
          ? 'none'
          : theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shorter,
            }),
        mr: { xs: open ? gridSpacing : 0 }  , // מרווח קבוע מעל md
        ml: 0,
        '& .MuiDrawer-paper': {
          height: (isMobile && isTemplatesPreview) ? '80vh' : '100%', // 80% height only for templates preview on mobile
          width: isMobile ? '90vw' : width, // 90% of viewport width on mobile
          maxWidth: isMobile ? '400px' : 'none', // Maximum 400px on mobile
          transition: isTemplatesPreview
            ? 'none'
            : open
              ? `transform 400ms cubic-bezier(0, 0, 0.2, 1) 0s, width ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.sharp} 0s !important`
              : undefined,
          boxSizing: 'border-box',
          position: 'relative',
          border: 'none',
          borderRadius: { sx: 0, md: `${borderRadius}px` },
          overflow: 'hidden',
          ...(isMobile && isTemplatesPreview && {
            top: '10%',
            transform: 'translateY(-50%)',
            margin: 'auto',
          }),
        },
      }}
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="right"
      open={open}
      ModalProps={{ 
        keepMounted: true,
        // Ensure backdrop click closes the sidebar on mobile
        onBackdropClick: () => (onClose ? onClose() : closeRightSidebar()),
      }}
      onClose={() => (onClose ? onClose() : closeRightSidebar())}
      transitionDuration={isTemplatesPreview ? 0 : theme.transitions.duration.shorter + 200}
    >
      <MainCard
        sx={{
          bgcolor: { xs: 'transparent', [theme.breakpoints.down('md')]: drawerBG },
          height: '100%',
          py: 1.25,
          border: 'none',
        }}
        border={theme.palette.mode !== ThemeMode.DARK}
        content={false}
      >
        <Stack direction="column" height="100%">
          {/* Default header */}
          {!hideHeader && (
             <Box
               sx={{
                 px: { xs: 1.5, md: 2.5 }, // Less padding on mobile
               }}
             >
              <Box
                sx={{
                  pb: 1.25,
                }}
              >
                <Grid container spacing={1.5} alignItems="center" width="100%">
                  <Grid size="grow" overflow="hidden">
                    <Typography
                      variant="h4"
                      color="text.primary"
                      fontWeight={500}
                      sx={{ ...ellipsisTextOverflowStyles }}
                    >
                      {title}
                    </Typography>
                  </Grid>
                  <Grid size="auto">
                    <IconButton 
                      onClick={() => (onClose ? onClose() : closeRightSidebar())}
                      sx={{
                        color: 'text.primary',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        // Ensure it's accessible on mobile
                        minWidth: { xs: 44, md: 'auto' },
                        minHeight: { xs: 44, md: 'auto' },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
              <Divider />
            </Box>
          )}
          {/* Sidebar body */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
             <Box
               id={'right-sidebar-content-wrapper'}
               sx={{
                 height: '100%',
                 overflow: 'auto',
                 pt: { xs: 1.5, md: 2.5 }, // Less padding on mobile
                 px: { xs: 1.5, md: 2.5 }, // Less padding on mobile
                 pb: 0.5,
               }}
             ></Box>
             
             {/* Floating close button for templates preview on mobile */}
             {isMobile && isTemplatesPreview && (
               <Fab
                 size="small"
                 onClick={() => (onClose ? onClose() : closeRightSidebar())}
                 sx={{
                   position: 'absolute',
                   top: 16,
                   right: 16,
                   zIndex: 1000,
                   backgroundColor: 'background.paper',
                   color: 'text.primary',
                   boxShadow: 3,
                   '&:hover': {
                     backgroundColor: 'action.hover',
                     boxShadow: 6,
                   },
                   border: '1px solid',
                   borderColor: 'divider',
                 }}
               >
                 <CloseIcon fontSize="small" />
               </Fab>
             )}
          </Box>
        </Stack>
      </MainCard>
    </Drawer>
  );
};

export default RightAppSidebar; 